import * as React from "react";
import { useCallback, useState, useEffect } from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
// import { renderRichText } from "gatsby-source-contentful/rich-text";
import PageContainer from "../pageContainer";
import { SectionTitle } from "../typography";
import { useInView } from "react-intersection-observer";
import { when } from "../../styles/theme";
import { GatsbyImage } from "gatsby-plugin-image";
import { format, parseISO } from "date-fns";
import useEmblaCarousel from "embla-carousel-react";
import { Button, ButtonRightIcon } from "../button";
import { useLocalization } from "gatsby-theme-i18n";
import Link from '../link.js';

export const fragment = graphql`
  fragment FinancialReportsContent on ContentfulPageSectionFinancialReports {
    title
    financialReports {
      cover {
        gatsbyImageData(layout: CONSTRAINED, width: 460)
        description
      }
      date
      title
      file {
        publicUrl
      }
    }
    internal {
      type
    }
  }
`;

const translations = {
  en: {
    more: "Show More",
  },
  zh: {
    more: "展示更多",
  },
};

const FinancialReportsSection = styled(PageContainer)`
  margin-top: 10rem;

  ${when("md")} {
    margin-top: 15rem;
  }
`;

const CarouselWrapper = styled.div`
  overflow: hidden;
  position: relative;
  max-width: 100vw;
  margin-top: 5rem;
  margin-left: -${(props) => props.theme.pageContainerWidth.xs}rem;
  margin-right: -${(props) => props.theme.pageContainerWidth.xs}rem;

  ${when("sm")} {
    margin-left: -${(props) => props.theme.pageContainerWidth.sm}rem;
    margin-right: -${(props) => props.theme.pageContainerWidth.sm}rem;
  }
  
  ${when("md")} {
    margin-top: 6rem;
  }
`;

const Reports = styled.div`
  display: flex;
  /* margin-left: -1rem;
  margin-right: -1rem;

  ${when("md")} {
    margin-left: -2rem;
    margin-right: -2rem;
  } */
`;

const Report = styled.a`
  flex: 0 0 60%;
  position: relative;
  margin-left: ${(props) => props.theme.gutterWidth.xs}rem;
  margin-right: ${(props) => props.theme.gutterWidth.xs}rem;

  ${when("sm")} {
    margin-left: ${(props) => props.theme.gutterWidth.sm}rem;
    margin-right: ${(props) => props.theme.gutterWidth.sm}rem;
  }

  ${when("md")} {
    flex: 0 0 30%;
    margin-left: ${(props) => props.theme.gutterWidth.md}rem;
    margin-right: ${(props) => props.theme.gutterWidth.md}rem;
  }

  &:hover {
    opacity: unset;
  }
`;

const ReportInnerContainer = styled.div`
  transform: ${(props) => (props.isActive ? "none" : "scale(0.8)")};
  opacity: ${(props) => (props.isActive ? 1 : 0.6)};
  transition: transform 0.25s ease-in-out, opacity 0.25s ease-in-out;
`;

const CoverContainer = styled.div``;

const Cover = styled(GatsbyImage)`
  border-radius: 1rem;
  overflow: hidden;
  -webkit-transform: translateZ(0);
  box-shadow: -3px 11px 18px 6px rgba(177, 177, 177, 0.81);
`;

const DescriptionContainer = styled.div`
  // margin-left: ${(props) => props.theme.gutterWidth.xs}rem;
  text-align: center;
  margin-top: 1.2rem;

  // ${when("sm")} {
  //   // margin-left: ${(props) => props.theme.gutterWidth.sm}rem;
  //   //align-self: center;
  // }
  //
  // ${when("md")} {
  //   // margin-left: ${(props) => props.theme.gutterWidth.sm}rem;
  // }
`;

const ReportTitle = styled.h3`
  margin: 0;
  ${when("sm")} {
    font-size: 2.4rem;
  }
`;

const ReportDate = styled.div`
  color: ${(props) => props.theme.colors.darkGrey};
  font-size: 1.2rem;

  ${when("sm")} {
    font-size: 1.4rem;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  pointer-events: none;
`;

const LeftControl = styled.div`
  position: absolute;
  top: 50%;
  left: 20%;
  padding: 1rem;
  transform: translate(-4.5rem, -3rem);
  cursor: pointer;
  pointer-events: auto;

  ${when("md")} {
    left: 0;
    transform: translate(5rem, -3rem);
  }
`;

const Left = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 1.25rem 1.25rem 1.25rem 0;
  border-color: transparent ${(props) => props.theme.colors.red} transparent
    transparent;
`;

const Right = styled(Left)`
  transform: scaleX(-1);
`;

const RightControl = styled(LeftControl)`
  left: unset;
  right: 20%;
  transform: translate(4.5rem, -3rem);

  ${when("md")} {
    right: 0;
    transform: translate(-5rem, -3rem);
  }
`;

const ButtonContainer = styled.div`
  margin-top: 4rem;
  display: flex;
  justify-content: center;
`;

const FinancialReports = ({ data, index, setCurrentSection }) => {
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && setCurrentSection) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    // loop: true,
  });

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on("select", (eventName) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
      });
    }
  }, [emblaApi]);

  const { title, financialReports } = data;

  const { locale } = useLocalization();

  const translation = translations[locale];

  return (
    <FinancialReportsSection
      as={"section"}
      hasSideBar={true}
      ref={ref}
      id={`section-${index}`}
    >
      <SectionTitle>{title}</SectionTitle>
      <CarouselWrapper ref={emblaRef}>
        <Reports>
          {financialReports.map((financialReport, index) => {
            const date = parseISO(financialReport.date);
            return (
              <Report
                key={index}
                href={financialReport.file?.publicUrl}
                target={"_blank"}
              >
                <ReportInnerContainer isActive={index === selectedIndex}>
                  <CoverContainer>
                    <Cover
                      alt={financialReport.cover?.description}
                      image={financialReport.cover?.gatsbyImageData}
                    />
                  </CoverContainer>
                  <DescriptionContainer>
                    <ReportTitle>{financialReport.title}</ReportTitle>
                    <ReportDate>{format(date, "dd.MM.yyyy")}</ReportDate>
                  </DescriptionContainer>
                </ReportInnerContainer>
              </Report>
            );
          })}
        </Reports>
        <Controls>
          <LeftControl onClick={scrollPrev}>
            <Left />
          </LeftControl>
          <RightControl onClick={scrollNext}>
            <Right />
          </RightControl>
        </Controls>
      </CarouselWrapper>
      <ButtonContainer>
        <Button as={Link} href="/investor-relationship/financial-reports/1">
          {translation["more"]}
          <ButtonRightIcon />
        </Button>
      </ButtonContainer>
    </FinancialReportsSection>
  );
};
export const financialReportsName = "ContentfulPageSectionFinancialReports";

export default FinancialReports;
